import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inputs, DEFAULT_INPUTS, calculateModel } from '../utils/roiCalculations';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { RecruitingRetentionMetrics } from '../components/RecruitingRetentionMetrics';
import { BookingModal } from '../components/BookingModal';
import { fmtMoney, fmtPct } from '../utils/formatters';
interface ResultsPageProps {
  isPreview?: boolean;
}
export function ResultsPage({
  isPreview = false
}: ResultsPageProps) {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  useEffect(() => {
    if (isPreview) {
      const loadInputs = () => {
        const stored = localStorage.getItem('previewInputs');
        if (stored) {
          try {
            setInputs(JSON.parse(stored));
          } catch (e) {
            console.error('Failed to parse preview inputs', e);
          }
        }
      };
      loadInputs();
      const interval = setInterval(loadInputs, 500);
      return () => clearInterval(interval);
    } else {
      const stored = localStorage.getItem('calculatorInputs');
      if (stored) {
        try {
          setInputs(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse calculator inputs', e);
        }
      }
    }
  }, [isPreview]);
  const results = useMemo(() => {
    return calculateModel(inputs);
  }, [inputs]);
  // Calculate adjusted breakeven based on halfSales and doubleAttrition toggles
  const adjustedBreakeven = useMemo(() => {
    if (!halfSales && !doubleAttrition) {
      return results.breakeven;
    }
    // Recalculate cumulative profit with adjustments
    let cumProfit = 0;
    // Start from index 1 to skip the "Launch" row
    for (let i = 1; i < results.rows.length; i++) {
      const row = results.rows[i];
      let adjustedGrossProfit = row.grossProfit;
      if (halfSales && doubleAttrition) {
        adjustedGrossProfit = row.grossProfit * 0.5 * 0.5;
      } else if (halfSales) {
        adjustedGrossProfit = row.grossProfit * 0.5;
      } else if (doubleAttrition) {
        adjustedGrossProfit = row.grossProfit * 0.5;
      }
      const adjustedNetProfit = adjustedGrossProfit - row.totalCost;
      cumProfit += adjustedNetProfit;
      if (cumProfit >= 0) {
        return row.period;
      }
    }
    return 'Not within 12 months';
  }, [results, halfSales, doubleAttrition]);
  // Calculate retention metrics - MATCH RetentionBreakdown.tsx exactly
  const adSpend = inputs.adSpend || 4000;
  const cph = inputs.cph || 500;
  const estimatedHires = Math.round(adSpend / cph);
  const estimatedLaunched = Math.round(estimatedHires * 0.7);
  const estimatedActive30 = estimatedLaunched * 0.3;
  const estimatedActive60 = estimatedActive30 * 0.6;
  const estimatedActive90 = estimatedActive60 * 0.8;
  // Calculate growing veteran population (90d+ reps) - includes ongoing retention
  const estimatedActive90Plus = estimatedActive90 + estimatedActive90 * 0.3;
  const estimatedActive1Year = estimatedActive90 * 0.3;
  const testimonials = [{
    name: 'Alden',
    company: 'Premier Roofing',
    role: 'Executive',
    quote: "They helped us hire nearly 100 new sales reps in one season, and many are now among our top performers. I have confidence knowing I don't just have a recruiting vendor. I have a true business partner.",
    initials: 'A',
    color: 'bg-blue-500'
  }, {
    name: 'Nolan',
    company: 'Elite Windows',
    role: 'Market Owner',
    quote: 'It took the headache out of recruiting and gave me my time back. In our first month, we brought on eight new reps. Three months later, six of them are still here and producing. We are now the top office in the company.',
    initials: 'N',
    color: 'bg-green-500'
  }, {
    name: 'Russell',
    company: 'Shared Solar Advisors',
    role: 'VP of Sales',
    quote: 'They helped us tighten our back-end so we could grow based on real, reliable, predictable systems instead of hopes, dreams, and shoestrings. We stopped second-guessing our training and started keeping the right people.',
    initials: 'R',
    color: 'bg-purple-500'
  }];
  const BookCallButton = () => <button onClick={() => setIsBookingModalOpen(true)} className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-base md:text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-861">
      Book My Free Strategy Call
    </button>;
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-862">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-863">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-center" data-id="element-864">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-id="element-865" />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6" data-id="element-866">
        {/* Hero Section */}
        <div id="hero" className="text-center mb-8 md:mb-12" data-id="element-867">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-868">
            Your Personalized ROI Projection
          </h1>
          <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 max-w-3xl mx-auto" data-id="element-869">
            {inputs.companyName}, here's what your recruiting investment could
            generate over the next 12 months
          </p>
        </div>

        {/* Results Summary */}
        <div id="summary" className="mb-8 md:mb-12" data-id="element-870">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-871">
            Your Numbers at a Glance
          </h2>
          <ResultsSummary results={results} breakeven={adjustedBreakeven} data-id="element-872" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-873">
            <BookCallButton data-id="element-874" />
          </div>
        </div>

        {/* Recruiting & Retention Metrics */}
        <div id="metrics" className="mb-8 md:mb-12" data-id="element-875">
          <RecruitingRetentionMetrics estimatedHires={estimatedHires} estimatedLaunched={estimatedLaunched} estimatedActive30={estimatedActive30} estimatedActive90={estimatedActive90Plus} estimatedActive1Year={estimatedActive1Year} data-id="element-876" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-877">
            <BookCallButton data-id="element-878" />
          </div>
        </div>

        {/* ROI Chart */}
        <div id="roi-chart" className="mb-8 md:mb-12" data-id="element-879">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-880">
            Your 12-Month ROI Trajectory
          </h2>
          <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-881" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-882">
            <BookCallButton data-id="element-883" />
          </div>
        </div>

        {/* Detailed Projections */}
        <div id="projections" className="mb-8 md:mb-12" data-id="element-884">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-885">
            Month-by-Month Breakdown
          </h2>
          <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={setDoubleAttrition} data-id="element-886" />
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-887">
            <BookCallButton data-id="element-888" />
          </div>
        </div>

        {/* Key Insights */}
        <div id="insights" className="mb-8 md:mb-12" data-id="element-889">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-890">
            Key Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-891">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-892">
              <div className="text-3xl md:text-4xl font-bold text-amber mb-2" data-id="element-893">
                {adjustedBreakeven === 'Launch' ? 'Upon Launch' : adjustedBreakeven}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-894">
                Break-Even Point
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-895">
                When your revenue exceeds your total investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-896">
              <div className="text-3xl md:text-4xl font-bold text-green-600" data-id="element-897">
                {fmtPct(results.roi)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-898">
                12-Month ROI
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-899">
                Return on your recruiting investment
              </p>
            </div>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-900">
              <div className="text-3xl md:text-4xl font-bold text-navy" data-id="element-901">
                {results.hiresPerMonth.toFixed(0)}
              </div>
              <div className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-902">
                Hires Per Month
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-2" data-id="element-903">
                Consistent, predictable hiring volume
              </p>
            </div>
          </div>
          <div className="flex justify-center mt-6 md:mt-8" data-id="element-904">
            <BookCallButton data-id="element-905" />
          </div>
        </div>

        {/* Testimonials */}
        <div id="testimonials" className="mb-8 md:mb-12" data-id="element-906">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-907">
            What Our Clients Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-908">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 md:p-6 shadow-lg" data-id="element-909">
                <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4" data-id="element-910">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full ${testimonial.color} flex items-center justify-center text-white font-bold text-lg md:text-xl`} data-id="element-911">
                    {testimonial.initials}
                  </div>
                  <div data-id="element-912">
                    <div className="font-bold text-navy text-sm md:text-base" data-id="element-913">
                      {testimonial.name}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600" data-id="element-914">
                      {testimonial.role}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-915">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-700 italic" data-id="element-916">
                  "{testimonial.quote}"
                </p>
              </div>)}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-br from-navy to-navy-light rounded-lg p-6 md:p-12 text-center text-white shadow-xl mb-8 md:mb-12" data-id="element-917">
          <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4" data-id="element-918">
            Ready to Achieve These Results?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 max-w-2xl mx-auto" data-id="element-919">
            Book a free strategy call to discuss how HIRE can help you build a
            high-performing sales team with predictable ROI.
          </p>
          <button onClick={() => setIsBookingModalOpen(true)} className="inline-block px-6 md:px-8 py-3 md:py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg md:text-xl rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-920">
            Book My Free Strategy Call
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-navy border-t-2 border-white/10" data-id="element-921">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-id="element-922">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6" data-id="element-923">
            <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 cursor-pointer" onClick={() => navigate('/')} data-id="element-924" />
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-blue-200" data-id="element-925">
              <button onClick={() => scrollToSection('hero')} className="hover:text-white transition-colors" data-id="element-926">
                Overview
              </button>
              <button onClick={() => scrollToSection('summary')} className="hover:text-white transition-colors" data-id="element-927">
                Summary
              </button>
              <button onClick={() => scrollToSection('metrics')} className="hover:text-white transition-colors" data-id="element-928">
                Metrics
              </button>
              <button onClick={() => scrollToSection('roi-chart')} className="hover:text-white transition-colors" data-id="element-929">
                ROI Chart
              </button>
              <button onClick={() => scrollToSection('projections')} className="hover:text-white transition-colors" data-id="element-930">
                Projections
              </button>
              <button onClick={() => scrollToSection('insights')} className="hover:text-white transition-colors" data-id="element-931">
                Insights
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors" data-id="element-932">
                Testimonials
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <BookingModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} data-id="element-933" />
    </div>;
}